var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef, } from 'react';
import BottomPanel from './BottomPanel';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import customEventHandler from '../helper/customEvents';
import { v4 as uuidv4 } from 'uuid';
import { saveState } from '../helper/stateManagerHelper';
import { playAudio } from '../helper/audioHelper';
import '../styles/rightSideToolPanel.scss';
import { useOnResize } from '../helper/hooks/onResize';
import LinkingCubesToolbar from '../widgets/LinkingCubesToolbar';
import CoinsAndCountersToolbar from '../widgets/CoinsAndCountersToolbar';
import TokenSettingPanel from './TokenSettingPanel';
import DraggableTokenList from './DraggableTokenList';
import { TooltipPortalContext } from './TooltipPortalContext';
import { NMP_PIECE_TYPE_CONFIG, isNmpAction } from '../constants/nmpConstants';
import { getGroupRightConfigKey } from '../helper/groupHelper';
import { useContainerRoving } from '../helper/hooks/useRovingTabIndex';
import { announce } from '../helper/announcer';
import { t } from 'i18next';
import { getVisibleMidpoint, findFreeDropPosition } from '../helper/viewportHelper';
import { getImageSrc } from '../helper/imageBlobCache';
import { entityToManipulative } from '../middleware/widgetMerger';
var RightSideToolPanel = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var collapsibleState = _a.collapsibleState, tokenSelections = _a.tokenSelections, setTokenSelections = _a.setTokenSelections, tokenPanelOpen = _a.tokenPanelOpen, setTokenPanelOpen = _a.setTokenPanelOpen, _l = _a.hideTrayCounter, hideTrayCounter = _l === void 0 ? false : _l;
    var _m = useContext(DataContext), selectedTool = _m.selectedTool, tool = _m.tool, annotations = _m.annotations, manipulative = _m.manipulative, setSelectedTool = _m.setSelectedTool, setManipulative = _m.setManipulative, setManiplulatorComponentVisible = _m.setManiplulatorComponentVisible, setActiveManipulator = _m.setActiveManipulator, selectedManipulativeId = _m.selectedManipulativeId, table = _m.table, popupReturnFocusRef = _m.popupReturnFocusRef, getPositionForNewItem = _m.getPositionForNewItem, contextMenu = _m.contextMenu;
    var _o = globalStore.useStoreState(function (state) { return state.jsonDataStore; }), btnConfig = _o.btnConfig, rightConfigStore = _o.rightConfig, uiAssets = _o.uiAssets;
    var viewEditMode = globalStore.useStoreState(function (state) { return state.playerStore.viewEditMode; });
    var isViewMode = viewEditMode === 'view';
    var rightPanelRoving = useContainerRoving({ orientation: 'vertical' });
    var _p = useState(true), isOpen = _p[0], setIsOpen = _p[1];
    var _q = useState(false), sliderTransitionEnabled = _q[0], setSliderTransitionEnabled = _q[1];
    // Mirrors `hasContent` (declared later) for the mount-only event listener
    // effect below, which can't close over the current `hasContent` value directly.
    var hasContentRef = useRef(false);
    var OpenIconSrc = uiAssets === null || uiAssets === void 0 ? void 0 : uiAssets.drawerIconOpen;
    var ClosedIconSrc = uiAssets === null || uiAssets === void 0 ? void 0 : uiAssets.drawerIconClosed;
    // Track actionType for number charts to set button active states
    var _r = useState({}), numberChartActionTypes = _r[0], setNumberChartActionTypes = _r[1];
    var _s = useState({}), pos = _s[0], setPos = _s[1];
    // Listen for actionType changes from NumberCharts
    useEffect(function () {
        var handleActionTypeChange = function (data) {
            setNumberChartActionTypes(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[data.id] = data.actionType, _a)));
            });
        };
        var handleTogglePanel = function (data) {
            // Don't let this force the drawer open when there's nothing to show
            // for the current selection (e.g. toggling the left "Math Tools"
            // header while a mixed/unsupported selection is active) — closing
            // is always honored, opening only when there's real content.
            if (data.isOpen && !hasContentRef.current)
                return;
            setIsOpen(data.isOpen);
        };
        // A11y: force-open the panel when context menu "Open Configuration" is clicked
        var handleOpenRightDrawer = function () {
            setIsOpen(true);
        };
        // A11y: open the panel AND move keyboard focus inside it. Used by the
        // linking-cube context menu's "Open right panel" action so the user can
        // jump from a placed cube straight into the contextual tools drawer.
        // We re-issue the focus a few times because the context menu's
        // `closeMenu` schedules its own setTimeout(0) to restore focus to the
        // trigger cube, and the drawer's slide-in transition needs time to
        // complete before the inner buttons become reliably focusable.
        var handleFocusRightPanel = function () {
            setIsOpen(true);
            var focusInside = function () {
                // Target the LinkingCubesToolbar element itself (role="toolbar" with
                // aria-label). Screen reader announces the toolbar landmark, and the
                // user can then arrow into the contained controls. Make it
                // programmatically focusable via tabindex=-1 if it isn't already.
                var toolbar = document.querySelector('#rightSideDrawerContainer .linkingCubesToolbar');
                if (toolbar) {
                    if (!toolbar.hasAttribute('tabindex')) {
                        toolbar.setAttribute('tabindex', '-1');
                    }
                    toolbar.focus();
                    return;
                }
                // Fallback: focus the drawer wrapper if the toolbar hasn't mounted.
                var container = document.getElementById('rightSideDrawerContainer');
                if (container) {
                    if (!container.hasAttribute('tabindex')) {
                        container.setAttribute('tabindex', '-1');
                    }
                    container.focus();
                }
            };
            // Multiple attempts spaced to bracket: closeMenu's setTimeout(0) for
            // restoring trigger focus, the 300ms drawer transition, and toolbar
            // late-mount. Each call re-focuses, winning against any prior focus
            // restoration (e.g. context menu's trigger-focus).
            [50, 200, 400, 600, 900].forEach(function (delay) {
                setTimeout(focusInside, delay);
            });
        };
        customEventHandler.addEventListener('numberChartActionType', handleActionTypeChange);
        customEventHandler.addEventListener('toggleRightSidePanel', handleTogglePanel);
        customEventHandler.addEventListener('openRightDrawer', handleOpenRightDrawer);
        customEventHandler.addEventListener('focusRightSidePanel', handleFocusRightPanel);
        return function () {
            customEventHandler.removeEventListener('numberChartActionType', handleActionTypeChange);
            customEventHandler.removeEventListener('toggleRightSidePanel', handleTogglePanel);
            customEventHandler.removeEventListener('openRightDrawer', handleOpenRightDrawer);
            customEventHandler.removeEventListener('focusRightSidePanel', handleFocusRightPanel);
        };
    }, []);
    // 1. GET ZOOM LEVEL
    var _t = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _t.displayMode, playerIconZoom = _t.playerIconZoom, zoomLevel = _t.zoomLevel, isIDM = _t.isIDM, isSwp = _t.isSwp, activityMeta = _t.activityMeta, language = _t.language, nmpAnimationEnabled = _t.nmpAnimationEnabled;
    // Dedupe tray counters by counterId — the source JSON can repeat the same
    // counter (e.g. half-dollar twice), which both shows a redundant palette item
    // and collides the React `key={counterId}` below. Keep the first occurrence.
    var trayCounters = useMemo(function () {
        var _a;
        var source = (isIDM || isSwp) && !hideTrayCounter ? ((_a = activityMeta === null || activityMeta === void 0 ? void 0 : activityMeta.trayCounters) !== null && _a !== void 0 ? _a : []) : [];
        var seen = new Set();
        return source.filter(function (tc) {
            if (seen.has(tc.counterId))
                return false;
            seen.add(tc.counterId);
            return true;
        });
    }, [isIDM, isSwp, hideTrayCounter, activityMeta === null || activityMeta === void 0 ? void 0 : activityMeta.trayCounters]);
    var hasTrayCounters = (isIDM || isSwp) && !hideTrayCounter && trayCounters.length > 0;
    var handleTrayCounterClick = useCallback(function (counterId, dropPosition) {
        var _a;
        // Click adds the counter at the visible viewport midpoint (then nudges
        // off any existing widget). Drag passes a `dropPosition` instead, which
        // is the canvas-coord drop location — used as-is without nudging so the
        // counter lands exactly where the user released the pointer.
        var center = dropPosition !== null && dropPosition !== void 0 ? dropPosition : getVisibleMidpoint(zoomLevel);
        // Build the counter first so we know its final dimensions (tray counter
        // sizes vary).
        var candidate = entityToManipulative({
            counterId: counterId,
            type: 'counter',
            data: { position: center, angle: 0, showImage: 'front' },
        }, trayCounters);
        var candidateData = candidate.data;
        var size = (_a = candidateData.dimensions) !== null && _a !== void 0 ? _a : { width: 60, height: 60 };
        var finalCenter = dropPosition !== null && dropPosition !== void 0 ? dropPosition : findFreeDropPosition(center, size, manipulative);
        var newItem = __assign(__assign({}, candidate), { data: __assign(__assign({}, candidateData), { center: finalCenter }) });
        saveState({
            type: newItem.data.id,
            id: newItem.data.id.split('_')[1],
            propertyName: 'create',
            old: JSON.stringify(newItem),
        });
        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem], false); });
        playAudio('CounterAdd');
    }, [zoomLevel, trayCounters, manipulative, setManipulative]);
    // Listen for tray-counter drops dispatched by WhiteBoard's drop handler.
    // The payload carries the counterId + canvas-coord position; the panel
    // owns the tray-counter→manipulative conversion so the logic stays in
    // one place.
    useEffect(function () {
        var handler = function (data) {
            handleTrayCounterClick(data.counterId, data.position);
        };
        customEventHandler.addEventListener('trayCounterDrop', handler);
        return function () { return customEventHandler.removeEventListener('trayCounterDrop', handler); };
    }, [handleTrayCounterClick]);
    // Dynamic column count for the tray counter list. We can't rely on CSS
    // auto-fit alone because `.drawer-content` uses `width: fit-content`
    // and ends up sized by whatever its widest sibling (BottomPanel /
    // LinkingCubesToolbar) settles on — so the tray list never gets a
    // standalone width signal. A ResizeObserver on the drawer measures the
    // resolved width and picks the largest column count that still gives
    // each item at least `CELL_MIN` of width. With more horizontal space
    // (LinkingCubesToolbar in the drawer, big screen, etc.) the tray
    // expands to 3 / 4 / 5+ columns; on narrow drawers it collapses back
    // to 1.
    var trayListRef = useRef(null);
    var _u = useState(1), trayColumns = _u[0], setTrayColumns = _u[1];
    // Multi-column mode is gated on whether something else is driving the
    // drawer width — a manipulative selection (BottomPanel tools) or linking
    // cubes on the canvas (LinkingCubesToolbar). Without that, the empty
    // BottomPanel's `min-width` would still leave enough room for 2 columns
    // and the tray list would never collapse back after the user deselects.
    var trayCanBeMultiCol = selectedTool.length > 0 ||
        manipulative.some(function (m) { return m.data.type === 'linkingCubes' || m.type === 'linkingCubes'; });
    useEffect(function () {
        if (!trayCanBeMultiCol) {
            setTrayColumns(1);
            return;
        }
        var el = trayListRef.current;
        if (!el)
            return;
        // Each cell needs ≥ CELL_MIN of width and the grid uses GAP between
        // adjacent cells. Available content width = el.contentRect.width
        // (border-box → already inside the .trayCounterList padding).
        var CELL_MIN = 48;
        var GAP = 8;
        var computeColumns = function (width) {
            // n columns fit when n * CELL_MIN + (n - 1) * GAP <= width
            // → n <= (width + GAP) / (CELL_MIN + GAP)
            var fit = Math.floor((width + GAP) / (CELL_MIN + GAP));
            return Math.max(1, fit);
        };
        var apply = function (width) {
            var next = computeColumns(width);
            setTrayColumns(function (prev) { return (prev === next ? prev : next); });
        };
        apply(el.getBoundingClientRect().width);
        var observer = new ResizeObserver(function (entries) {
            var entry = entries[0];
            if (!entry)
                return;
            apply(entry.contentRect.width);
        });
        observer.observe(el);
        return function () { return observer.disconnect(); };
    }, [hasTrayCounters, trayCanBeMultiCol, selectedTool.length]);
    // 2. DEFINE LARGE MODE (Active if zoom is greater than 1)
    var isLargeMode = playerIconZoom > 1;
    // 3. DETERMINE CURRENT TOOL CONFIG (Standard Logic)
    var rightConfig = useMemo(function () {
        if (selectedTool.length > 0) {
            // Handle manipulativeGroup_ selections — resolve via group members (single or multi-group)
            var allGroups = selectedTool.length >= 1 && selectedTool.every(function (t) { return t.startsWith('manipulativeGroup_'); });
            if (allGroups) {
                var members = manipulative.filter(function (m) { var _a; return selectedTool.includes((_a = m.data.groupId) !== null && _a !== void 0 ? _a : ''); });
                var configKey = getGroupRightConfigKey(members);
                if (configKey)
                    return rightConfigStore[configKey] || null;
                // Mixed pieceTypes (e.g. numberYellow + numberColored) — show common tools
                var allNmp = members.every(function (m) { return m.type === 'numberPiece' || m.type === 'moneyPiece'; });
                if (allNmp && members.length > 0) {
                    var configs_1 = Array.from(new Set(members.map(function (m) { return NMP_PIECE_TYPE_CONFIG[m.data.pieceType]; })))
                        .filter(Boolean)
                        .map(function (key) { return rightConfigStore[key]; })
                        .filter(function (c) { return c != null; });
                    if (configs_1.length > 1) {
                        var commonConfig = configs_1[0].filter(function (item) {
                            return configs_1.every(function (config) {
                                return config.some(function (configItem) {
                                    return typeof item === 'string' && typeof configItem === 'string'
                                        ? item === configItem
                                        : item === configItem;
                                });
                            });
                        });
                        return commonConfig.length > 0 ? commonConfig : null;
                    }
                    if (configs_1.length === 1)
                        return configs_1[0];
                }
                return null;
            }
            var configs_2 = selectedTool
                .map(function (tool) {
                var _a;
                var toolKey = tool.split('_')[0];
                if (tool.includes('annotation')) {
                    var num = Number(tool.split('_')[1]);
                    // `annotations[num]` can be undefined briefly when an annotation
                    // is deleted while still in `selectedTool` — guard the `.type`
                    // read so the lookup just returns undefined and falls through
                    // to the null filter below.
                    return rightConfigStore[(_a = annotations[num]) === null || _a === void 0 ? void 0 : _a.type];
                }
                // For NMP pieces, resolve rightConfig key from pieceType
                var nmpItem = manipulative.find(function (m) { return m.data.id === tool; });
                if (nmpItem) {
                    var pieceType = nmpItem.data.pieceType;
                    var configKey = pieceType && NMP_PIECE_TYPE_CONFIG[pieceType];
                    if (configKey)
                        return rightConfigStore[configKey];
                }
                // Card-like tray counters (ship a back image) get a flip button in
                // the right panel, mirroring how cards/decks expose flip.
                if ((nmpItem === null || nmpItem === void 0 ? void 0 : nmpItem.data.type) === 'counter' && nmpItem.data.customImageBack) {
                    return rightConfigStore['trayCounterCard'];
                }
                if ((nmpItem === null || nmpItem === void 0 ? void 0 : nmpItem.data.type) === 'numberFrameCounter') {
                    return rightConfigStore['numberFrameCounter'];
                }
                // Jump bars and custom ticks don't have their own right-panel
                // entry — they inherit the parent number line's panel so the
                // user can keep adding/configuring jump bars, labels, masks,
                // and custom ticks while a child of the line is focused.
                if ((toolKey === 'jumpBar' || toolKey === 'customTick') &&
                    rightConfigStore['numberLine']) {
                    return rightConfigStore['numberLine'];
                }
                return rightConfigStore[toolKey];
            })
                .filter(function (config) { return config !== null && config !== undefined; });
            if (configs_2.length > 0) {
                if (configs_2.length === 1) {
                    return configs_2[0];
                }
                var commonConfig = configs_2[0].filter(function (item) {
                    return configs_2.every(function (config) {
                        return config.some(function (configItem) {
                            if (typeof item === 'string' && typeof configItem === 'string') {
                                return item === configItem;
                            }
                            return item === configItem;
                        });
                    });
                });
                return commonConfig.length > 0 ? commonConfig : null;
            }
        }
        var toolKey = tool;
        return rightConfigStore[toolKey] || null;
    }, [selectedTool, tool, rightConfigStore, annotations, manipulative]);
    // Diagnostic: when a tool is selected but no right-side panel config
    // resolves, log the details. The known production cause is a legacy/corrupt
    // saved `data.id` that is COLON-delimited (e.g. "numberLine:<uuid>") instead
    // of the underscore form ("numberLine_<uuid>") this component expects — the
    // config lookup does `tool.split('_')[0]`, so a colon id yields the whole
    // string as the key, misses `rightConfigStore`, and the panel silently fails
    // to open. `startsWith('numberLine_')` checks elsewhere break the same way.
    useEffect(function () {
        if (selectedTool.length === 0 || rightConfig != null)
            return;
        var resolvedKeys = selectedTool.map(function (t) { return t.split('_')[0]; });
        // A selected id whose split('_')[0] is not a known config key — and that
        // contains a ':' — is the colon-vs-underscore id-format mismatch.
        var malformedIds = selectedTool.filter(function (t) { return t.includes(':') && !rightConfigStore[t.split('_')[0]]; });
        console.error('[RIGHT_PANEL] no panel resolved for selection', {
            selectedTool: selectedTool,
            tool: tool,
            resolvedKeys: resolvedKeys,
            malformedIds: malformedIds,
            likelyColonIdMismatch: malformedIds.length > 0,
            hasNumberLineConfig: !!rightConfigStore['numberLine'],
            rightConfigStoreKeys: Object.keys(rightConfigStore),
        });
    }, [selectedTool, tool, rightConfig, rightConfigStore]);
    // 4. SETUP BUTTONS & MAP CHORAL CONFIG
    var setupToolButton = function (toolBtn) {
        if (!toolBtn)
            return [];
        return toolBtn
            .map(function (_tool) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (typeof _tool === 'string') {
                // A. Check if this is the Choral tool (using any common name you might have in rightConfig)
                // C. Standard handling for all other buttons
                var btnConfigItem = btnConfig[_tool];
                if (Array.isArray(btnConfigItem)) {
                    return btnConfigItem;
                }
                if (btnConfigItem) {
                    var buttonConfig = __assign(__assign({}, btnConfigItem), { id: _tool });
                    // Set activeState for showHideNumbers button based on actionType.
                    // Read from the manipulative's persisted data (not just the
                    // event-fed numberChartActionTypes state) since this panel
                    // unmounts on deselect and remounts fresh on reselect — no new
                    // 'numberChartActionType' event fires then because actionType
                    // itself hasn't changed, so the event-only state would be stale.
                    if (_tool === 'showHideNumbers' && selectedTool.length > 0) {
                        var currentToolId_1 = selectedTool[0];
                        if (currentToolId_1.startsWith('numberChart_')) {
                            var manipulativeItem = manipulative.find(function (item) { return item.data.id === currentToolId_1; });
                            var persistedActionType = (_a = manipulativeItem === null || manipulativeItem === void 0 ? void 0 : manipulativeItem.data) === null || _a === void 0 ? void 0 : _a.actionType;
                            var actionType = (_b = numberChartActionTypes[currentToolId_1]) !== null && _b !== void 0 ? _b : persistedActionType;
                            buttonConfig.activeState = actionType === 'clear';
                        }
                    }
                    // Sync toggle defaultValue with actual manipulative state
                    if (_tool === 'toggleExpression' && selectedTool.length > 0) {
                        var currentToolId_2 = selectedTool[0];
                        if (currentToolId_2.startsWith('arrayModel_') ||
                            currentToolId_2.startsWith('coloredTiles_')) {
                            var manipulativeItem = manipulative.find(function (item) { return item.data.id === currentToolId_2; });
                            if (manipulativeItem) {
                                var showExpression = (_c = manipulativeItem.data.showExpression) !== null && _c !== void 0 ? _c : true;
                                buttonConfig.defaultValue = showExpression ? 'text.on' : 'text.off';
                            }
                        }
                    }
                    if (_tool === 'toggleProduct' && selectedTool.length > 0) {
                        var currentToolId_3 = selectedTool[0];
                        if (currentToolId_3.startsWith('arrayModel_') ||
                            currentToolId_3.startsWith('coloredTiles_')) {
                            var manipulativeItem = manipulative.find(function (item) { return item.data.id === currentToolId_3; });
                            if (manipulativeItem) {
                                var showProduct = (_d = manipulativeItem.data.showProduct) !== null && _d !== void 0 ? _d : true;
                                buttonConfig.defaultValue = showProduct ? 'text.on' : 'text.off';
                            }
                        }
                    }
                    if (_tool === 'dimensionLabels' && selectedTool.length > 0) {
                        var currentToolId_4 = selectedTool[0];
                        if (currentToolId_4.startsWith('coloredTiles_')) {
                            var manipulativeItem = manipulative.find(function (item) { return item.data.id === currentToolId_4; });
                            if (manipulativeItem) {
                                var dimensionLabels = (_e = manipulativeItem.data.dimensionLabels) !== null && _e !== void 0 ? _e : false;
                                buttonConfig.defaultValue = dimensionLabels ? 'text.on' : 'text.off';
                            }
                        }
                    }
                    // Set activeState for partMode button for array models
                    if (_tool === 'partMode' && selectedTool.length > 0) {
                        var currentToolId_5 = selectedTool[0];
                        if (currentToolId_5.startsWith('arrayModel_') ||
                            currentToolId_5.startsWith('coloredTiles_')) {
                            var itemMatch = manipulative.find(function (item) { return item.data.id === currentToolId_5; });
                            if (itemMatch) {
                                buttonConfig.activeState = (_f = itemMatch.data.partModeEnabled) !== null && _f !== void 0 ? _f : true;
                            }
                        }
                    }
                    // Set activeState for selectMode button for array models
                    if (_tool === 'selectMode' && selectedTool.length > 0) {
                        var currentToolId_6 = selectedTool[0];
                        if (currentToolId_6.startsWith('arrayModel_') ||
                            currentToolId_6.startsWith('coloredTiles_')) {
                            var itemMatch = manipulative.find(function (item) { return item.data.id === currentToolId_6; });
                            if (itemMatch) {
                                buttonConfig.activeState = (_g = itemMatch.data.selectModeEnabled) !== null && _g !== void 0 ? _g : false;
                            }
                        }
                    }
                    // Sync show5x5Grid toggle with the selected number frame's data
                    if (_tool === 'show5x5Grid' && selectedTool.length > 0) {
                        var currentToolId_7 = selectedTool[0];
                        if (currentToolId_7.startsWith('numberFrame_')) {
                            var manipulativeItem = manipulative.find(function (item) { return item.data.id === currentToolId_7; });
                            buttonConfig.activeState = (_j = (_h = manipulativeItem === null || manipulativeItem === void 0 ? void 0 : manipulativeItem.data) === null || _h === void 0 ? void 0 : _h.show5x5Grid) !== null && _j !== void 0 ? _j : false;
                        }
                    }
                    // Toggle outline button between "Create Outline" (+) and "Clear Outline" (X)
                    // based on whether any outline shapes exist on the canvas
                    // (either static outlines from "Create Outline" or library outlines).
                    if (_tool === 'outline') {
                        var hasOutlines = manipulative.some(function (m) {
                            return m.data.type === 'patternShapes' &&
                                (m.data.isStaticOutline || m.data.isOutline);
                        });
                        if (hasOutlines) {
                            buttonConfig.id = 'clearOutline';
                            buttonConfig.label = 'text.removeOutline';
                            buttonConfig.ariaLabel = 'text.removeOutline';
                            buttonConfig.imgSrc = {
                                normal: './assets/images/manipulative/patternShape/outlineClearN.svg',
                                hover: './assets/images/manipulative/patternShape/outlineClearN.svg',
                                disable: './assets/images/manipulative/patternShape/outlineClearN.svg',
                            };
                        }
                        else {
                            buttonConfig.label = 'text.createOutline';
                            buttonConfig.ariaLabel = 'text.createOutline';
                        }
                    }
                    // Sync nmp_animation toggle with the global NMP animation flag.
                    // The flag is the single source of truth — no longer derived from
                    // the selected piece(s), so the button stays consistent across
                    // selection changes and even when nothing NMP-specific is selected.
                    if (isNmpAction(_tool, 'nmp_animation')) {
                        buttonConfig.defaultValue = nmpAnimationEnabled ? 'text.on' : 'text.off';
                    }
                    // Sync fractionOverlayShading toggle with selected clock(s)
                    if (_tool === 'fractionOverlayShading' && selectedTool.length > 0) {
                        var selectedClocks = selectedTool.filter(function (sid) { return sid.startsWith('analog_clock_'); });
                        if (selectedClocks.length > 0) {
                            var anyHasOverlay = selectedClocks.some(function (sid) {
                                var m = manipulative.find(function (item) { return item.data.id === sid; });
                                return m && m.data.fractionOverlayParts != null;
                            });
                            if (!anyHasOverlay) {
                                buttonConfig.defaultValue = 'text.off';
                            }
                            else {
                                var anyOff = selectedClocks.some(function (sid) {
                                    var m = manipulative.find(function (item) { return item.data.id === sid; });
                                    return m && m.data.fractionOverlayShadingEnabled !== true;
                                });
                                buttonConfig.defaultValue = anyOff ? 'text.off' : 'text.on';
                            }
                        }
                    }
                    return buttonConfig;
                }
                // console.warn(`RightSideToolPanel: btnConfig key "${_tool}" not found`);
                return null;
            }
            return _tool;
        })
            .flat()
            .filter(function (item) { return item !== null; });
    };
    var toolsBtn = useMemo(function () { return setupToolButton(rightConfig); }, [rightConfig, btnConfig, selectedTool, numberChartActionTypes, manipulative]);
    // Check if any selected tool is a number frame
    var isNumberFrameSelected = useMemo(function () {
        return selectedTool.some(function (toolId) { return toolId.startsWith('numberFrame_'); });
    }, [selectedTool]);
    // True once a real frame exists on the canvas. The initial "Number Frames" click
    // creates a "_dummyForRightTool" placeholder — the Add Frame button opens the
    // popup that turns it into a real frame; until then the token-setting buttons
    // and token-group items stay keyboard-inert.
    var hasRealNumberFrame = useMemo(function () {
        return manipulative.some(function (m) { return m.data.type === 'numberFrames' && !String(m.data.id).endsWith('_dummyForRightTool'); });
    }, [manipulative]);
    // True when a NumberFrame token (`numberFrameCounter`) is selected. Gates
    // the NumberFrame token UI (top/bottom token panels, draggable token list)
    // and the auto-open panel effect for that flow. Tray counters (`'counter'`)
    // are not part of the NumberFrame ecosystem — they get the panel opened via
    // the `hasContent` / `hasTrayCounters` effects below, not this flag.
    var isTrayCounterCardSelected = useMemo(function () {
        return selectedTool.some(function (toolId) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            return (item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && !!item.data.customImageBack;
        });
    }, [selectedTool, manipulative]);
    var isCounterSelected = useMemo(function () {
        return selectedTool.some(function (toolId) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            return (item === null || item === void 0 ? void 0 : item.data.type) === 'numberFrameCounter';
        });
    }, [selectedTool, manipulative]);
    // True only when an in-frame `numberFrameCounter` is selected. The
    // DraggableTokenList is a number-frame-specific control, so a plain
    // standalone `counter` selection should NOT surface it.
    var isNumberFrameCounterSelected = useMemo(function () {
        return selectedTool.some(function (toolId) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            return (item === null || item === void 0 ? void 0 : item.data.type) === 'numberFrameCounter';
        });
    }, [selectedTool, manipulative]);
    // Enable slider transition only after the first paint to avoid mount flicker
    useEffect(function () {
        if (!isNumberFrameSelected) {
            setSliderTransitionEnabled(false);
            return;
        }
        var id = requestAnimationFrame(function () { return setSliderTransitionEnabled(true); });
        return function () { return cancelAnimationFrame(id); };
    }, [isNumberFrameSelected]);
    // Close token panels when neither a number frame nor a counter is selected
    useEffect(function () {
        if (!isNumberFrameSelected && !isCounterSelected) {
            setTokenPanelOpen({ top: false, bottom: false });
        }
    }, [isNumberFrameSelected, isCounterSelected]);
    // Also close token panels whenever the drawer itself is closed — so the
    // next time the drawer opens (re-selecting a number frame), the token
    // panels start collapsed instead of remembering their prior-open state.
    useEffect(function () {
        if (!isOpen) {
            setTokenPanelOpen({ top: false, bottom: false });
        }
        return function () {
            setTokenPanelOpen({ top: false, bottom: false });
        };
    }, [isOpen]);
    // Check if any selected tool is an analog clock
    var isAnalogClockSelected = useMemo(function () {
        return selectedTool.some(function (toolId) {
            // Check if it's an analog clock ID (starts with 'analog_clock_')
            if (toolId.startsWith('analog_clock_')) {
                return manipulative.some(function (m) { return m.data.id === toolId && m.data.type === 'analog'; });
            }
            return false;
        });
    }, [selectedTool, manipulative]);
    // Coins & Counters drawer is "active" when the user is in coins mode —
    // selectedTool is the dummy id from the popup, or a placed counter
    // (`coinsAndCounters_<uuid>`). Click on empty canvas clears selectedTool,
    // unmounting the panel (matches Pattern Shapes' behavior — re-clicking
    // a placed counter re-mounts because the drawer state in context is
    // preserved).
    var isCoinsDrawerActive = (function () {
        var _a;
        if (!selectedTool.length)
            return false;
        // Fast path: individual coin or dummy ID (coins mode, no counters placed yet).
        if ((_a = selectedTool[0]) === null || _a === void 0 ? void 0 : _a.startsWith('coinsAndCounters_'))
            return true;
        // Mixed CC selection: all IDs are coins pieces or groups, all members are CC type.
        var allCCIds = selectedTool.every(function (id) { return id.startsWith('coinsAndCounters_') || id.startsWith('manipulativeGroup_'); });
        if (!allCCIds)
            return false;
        var resolvedMembers = manipulative.filter(function (m) {
            var _a, _b;
            if (!(m === null || m === void 0 ? void 0 : m.data))
                return false;
            return (selectedTool.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : '') ||
                selectedTool.includes((_b = m.data.groupId) !== null && _b !== void 0 ? _b : ''));
        });
        return resolvedMembers.length > 0 && resolvedMembers.every(function (m) { return m.data.type === 'coinsAndCounters'; });
    })();
    // `hasContent` gates several useEffects that auto-open / auto-close the
    // panel based on whether there's anything to show. The Coins & Counters
    // drawer doesn't go through `rightConfig`, so without this OR it would
    // fail those gates: the drawer wouldn't auto-open when clicking a placed
    // counter, and would auto-close when the user clicks elsewhere.
    var hasContent = Boolean(rightConfig && rightConfig.length > 0) || isCoinsDrawerActive;
    useEffect(function () {
        hasContentRef.current = hasContent;
    }, [hasContent]);
    // Track whether linking cubes exist on canvas
    var hasLinkingCubes = manipulative.some(function (m) { return m.data.type === 'linkingCubes' || m.type === 'linkingCubes'; });
    // Reset state and cleanup when config becomes empty so the panel tree can be fully destroyed (avoids detached DOM)
    // IDM sessions keep the panel open unconditionally (tray-counter list lives here).
    useEffect(function () {
        if (isIDM || isSwp)
            return;
        var isLinkingCubeTool = selectedTool.length > 0 && selectedTool.every(function (s) { return s.startsWith('linkingCube'); });
        if (!hasContent &&
            !hasLinkingCubes &&
            !isLinkingCubeTool &&
            !isNumberFrameSelected &&
            !isCounterSelected &&
            !hasTrayCounters) {
            setIsOpen(false);
        }
    }, [
        isIDM,
        hasContent,
        hasLinkingCubes,
        selectedTool,
        isNumberFrameSelected,
        isCounterSelected,
        hasTrayCounters,
    ]);
    // Auto-open panel when any manipulative is selected and there is
    // corresponding right-config. SUPPRESS this when a context menu is open —
    // the SelectFocusOverlay's clickHandler updates selection AND fires
    // onAfterClick (which opens the context menu) in the same event, so the
    // auto-open would slide the drawer in alongside the menu and steal SR
    // focus from the menu. The user is interacting with the menu; opening
    // another panel underneath is jarring on every platform.
    useEffect(function () {
        if (contextMenu)
            return;
        if (selectedTool.length > 0 && hasContent) {
            setIsOpen(true);
        }
    }, [selectedTool, hasContent, contextMenu]);
    // Auto-open panel when a number frame or any counter (standalone or
    // NumberFrame token) is selected. Same context-menu suppression as above.
    useEffect(function () {
        if (contextMenu)
            return;
        if (isNumberFrameSelected || isCounterSelected) {
            setIsOpen(true);
        }
    }, [isNumberFrameSelected, isCounterSelected, contextMenu]);
    // Auto-open panel when IDM tray counters are available
    useEffect(function () {
        if (hasTrayCounters) {
            setIsOpen(true);
        }
    }, [hasTrayCounters]);
    // Auto-open panel for any IDM/SWP session
    useEffect(function () {
        if (isIDM || isSwp) {
            setIsOpen(true);
        }
    }, [isIDM, isSwp]);
    // Listen for right-panel button clicks so we can create patternShapes items
    useEffect(function () {
        var handleBottomPanel = function (data) {
            // Allow creating pattern shapes when the right panel contains those buttons
            // even if `selectedTool` is not set (panel may be open via other means).
            var key = selectedTool.length > 0 ? selectedTool[0].split('_')[0] : null;
            var isPatternPanel = key === 'patternShapes' || toolsBtn.some(function (b) { return b.id === data.id; });
            if (!isPatternPanel)
                return;
            var shapeMap = {
                triangle_equilateral: { shapeType: 'triangle_equilateral', colorKey: 'GREEN' },
                triangle_right: { shapeType: 'triangle_right', colorKey: 'PURPLE' },
                square: { shapeType: 'square', colorKey: 'ORANGE' },
                rhombus_blue: { shapeType: 'rhombus_blue', colorKey: 'BLUE' },
                rhombus_white: { shapeType: 'rhombus_white', colorKey: 'WHITE' },
                trapezoid: { shapeType: 'trapezoid', colorKey: 'RED' },
                hexagon: { shapeType: 'hexagon', colorKey: 'YELLOW' },
            };
            // handle special right-panel buttons for patternShapes
            if (data.id === 'outlineLibrary') {
                // open the manipulative popup with our pattern shapes library
                setManiplulatorComponentVisible(true);
                setActiveManipulator('patternShapesLibrary');
                return;
            }
            if (data.id === 'outline') {
                // Show pattern shapes outline confirmation popup
                setActiveManipulator('patternOutlineConfirmation');
                setManiplulatorComponentVisible(true);
                return;
            }
            if (data.id === 'clearOutline') {
                // Show clear outline confirmation popup
                setActiveManipulator('patternClearOutlineConfirmation');
                setManiplulatorComponentVisible(true);
                return;
            }
            var mapping = shapeMap[data.id];
            if (!mapping)
                return;
            var _a = getPositionForNewItem(), x = _a.x, y = _a.y;
            // White rhombus starts tilted at 45° to match sidebar appearance and reference activity
            var initialRotation = mapping.shapeType === 'rhombus_white' ? 45 : 0;
            var newItem = {
                type: 'patternShapes',
                data: {
                    id: "patternShapes_".concat(uuidv4().replace(/-/g, '')),
                    type: 'patternShapes',
                    shapeType: mapping.shapeType,
                    center: { x: x, y: y },
                    rotation: initialRotation,
                    scale: 1,
                    color: mapping.colorKey,
                    lock: { positioning: false, editing: false },
                },
                widgetId: "patternShapes:".concat(uuidv4().replace(/-/g, '')),
            };
            saveState({
                type: newItem.data.id,
                id: newItem.data.id.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(newItem),
            });
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem], false); });
            playAudio('PatternAdd');
            // select newly created item so user can use bottom controls directly
            setSelectedTool([newItem.data.id]);
        };
        customEventHandler.addEventListener('bottomPanel', handleBottomPanel);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleBottomPanel);
        };
    }, [selectedTool, setManipulative, setSelectedTool, hasContent]);
    var onUpdateUI1 = function () {
        // Hardcoded layout constants — mirror the baseline sizes for 1920×1080.
        // Tweak these if the header/zoom/footer chrome changes. We no longer read
        // `.headerContainer` etc. because those elements aren't rendered in every
        // session (e.g. IDM without ManipulativeTool).
        var HEADER_HEIGHT = 70; // .appHeader / .headerContainer baseline (≈4.375rem)
        var HEADER_TOP = 0; // header sits at viewport top
        var ZOOM_PANEL_HEIGHT = 40;
        var FOOTER_HEIGHT = 70; // smartBoard footer baseline
        if (displayMode === 'normal') {
            setPos(function () { return ({
                top: HEADER_HEIGHT + HEADER_TOP,
                toreduce: HEADER_HEIGHT + HEADER_TOP + 26 + ZOOM_PANEL_HEIGHT,
            }); });
        }
        else {
            setPos(function () { return ({
                top: 26 + ZOOM_PANEL_HEIGHT,
                toreduce: isViewMode
                    ? 26 + ZOOM_PANEL_HEIGHT + FOOTER_HEIGHT
                    : 26 + ZOOM_PANEL_HEIGHT + FOOTER_HEIGHT + 30 + HEADER_HEIGHT,
            }); });
        }
    };
    var onUpdateUI = function () {
        if (displayMode === 'normal') {
            setTimeout(function () {
                var headerContainer = document.getElementsByClassName('headerContainer')[0];
                var zoomAndPanControlPanel = document.querySelector('#zoomAndPanControlPanel');
                var appHeader = document.getElementsByClassName('appHeader')[0];
                if (headerContainer) {
                    if (zoomAndPanControlPanel && headerContainer) {
                        var _bound_1 = headerContainer.getBoundingClientRect();
                        var _zoomBound_1 = zoomAndPanControlPanel.getBoundingClientRect();
                        setPos(function (prev) { return ({
                            top: _bound_1.height + _bound_1.top,
                            toreduce: _bound_1.height + _bound_1.top + 26 + _zoomBound_1.height,
                        }); });
                    }
                }
                else {
                    if (appHeader && zoomAndPanControlPanel) {
                        var _bound_2 = appHeader.getBoundingClientRect();
                        var _zoomBound_2 = zoomAndPanControlPanel.getBoundingClientRect();
                        setPos(function (prev) { return ({
                            top: _bound_2.height + _bound_2.top,
                            toreduce: _bound_2.height + _bound_2.top + 26 + _zoomBound_2.height,
                        }); });
                    }
                }
            }, 0);
        }
        else {
            setTimeout(function () {
                var headerContainer = document.getElementsByClassName('headerContainer')[0];
                var zoomAndPanControlPanel = document.querySelector('#zoomAndPanControlPanel');
                var footer = document.querySelector('.footer');
                if (headerContainer) {
                    if (zoomAndPanControlPanel && headerContainer) {
                        var _bound_3 = headerContainer.getBoundingClientRect();
                        var _zoomBound_3 = zoomAndPanControlPanel.getBoundingClientRect();
                        var rect_1 = footer.getBoundingClientRect();
                        setPos(function (prev) { return ({
                            top: 26 + _zoomBound_3.height,
                            toreduce: 26 + _zoomBound_3.height + rect_1.height + 30 + _bound_3.height,
                        }); });
                    }
                }
                else {
                    if (footer && zoomAndPanControlPanel) {
                        var _zoomBound_4 = zoomAndPanControlPanel.getBoundingClientRect();
                        var rect_2 = footer.getBoundingClientRect();
                        setPos(function (prev) { return ({
                            top: 26 + _zoomBound_4.height,
                            toreduce: 26 + _zoomBound_4.height + rect_2.height,
                        }); });
                    }
                }
            }, 0);
        }
    };
    useEffect(function () {
        onUpdateUI();
    }, [displayMode, zoomLevel, playerIconZoom, isOpen, isViewMode, language]);
    var handleResize = useCallback(function () {
        onUpdateUI();
    }, [displayMode, zoomLevel, playerIconZoom, isViewMode, language]);
    useOnResize(handleResize);
    // Track previous button IDs to skip redundant width recalculations
    // (e.g. when selectedTool changes from patternShapes_ to manipulativeGroup_ but same buttons)
    var prevBtnIdsRef = useRef('');
    // Track last applied language so a change forces a width recalc even when
    // button IDs are identical — translated labels can resize toggles/buttons.
    var lastLanguageRef = useRef(language);
    // Calculate and set explicit width for Safari/Firefox
    useEffect(function () {
        // Build a stable key from button IDs to detect actual changes
        var btnIdsKey = toolsBtn.map(function (t) { return t.id; }).join(',');
        var isSameButtons = btnIdsKey === prevBtnIdsRef.current;
        // Use setTimeout with RAF to ensure Safari has fully settled layout
        var timeoutId = setTimeout(function () {
            requestAnimationFrame(function () {
                var container = document.querySelector('.drawer-container');
                var drawerContent = document.querySelector('.drawer-content');
                var toolPanel = document.querySelector('#rightSideToolPanel');
                if (container && drawerContent && toolPanel && toolsBtn.length > 0) {
                    // If the button set hasn't changed and width is already set, skip recalculation.
                    // The lastLanguageRef check forces a recalc when the user switches EN↔ES, since
                    // translated labels can change toggle/button widths while button IDs stay the same.
                    if (isSameButtons && toolPanel.style.width && lastLanguageRef.current === language) {
                        return;
                    }
                    prevBtnIdsRef.current = btnIdsKey;
                    lastLanguageRef.current = language;
                    // Get the CSS variable for icon size as fallback
                    var computedStyle = getComputedStyle(container);
                    var iconSizeStr = computedStyle.getPropertyValue('--zoomed-icon-size') || '40px';
                    var iconSize_1 = parseFloat(iconSizeStr);
                    // Measure actual button wrapper dimensions from the DOM
                    var btnWrappers = toolPanel.querySelectorAll('.buttonWrapper, .iconBtnWrapper, .toggleBtnWrapper, .groupedBtnWrapper');
                    var items_1 = [];
                    btnWrappers.forEach(function (el) {
                        items_1.push({
                            w: el.offsetWidth || iconSize_1,
                            h: el.offsetHeight || iconSize_1,
                        });
                    });
                    // Fallback: if DOM wrappers aren't rendered yet, use icon size for all items
                    if (items_1.length === 0) {
                        for (var i = 0; i < toolsBtn.length; i++) {
                            items_1.push({ w: iconSize_1, h: iconSize_1 });
                        }
                    }
                    // Read actual gap from the panel's computed style (use longhands for Firefox/Safari)
                    var panelStyle = getComputedStyle(toolPanel);
                    var colGap = parseFloat(panelStyle.columnGap) || 10;
                    // Clear inline styles first so the browser renders the natural CSS layout.
                    // We then read offsetLeft from each item to detect actual columns.
                    toolPanel.style.width = '';
                    toolPanel.style.maxWidth = '';
                    toolPanel.style.minWidth = '';
                    // Read actual rendered column layout from DOM by clustering items by offsetLeft.
                    // align-items:center causes variation within a column (up to 24px for our icon range),
                    // so we cluster with a 30px tolerance. Column gaps are much larger (>50px between clusters).
                    var COL_CLUSTER_THRESHOLD_1 = 30;
                    var colWidthMap_1 = new Map(); // cluster key → max item width
                    btnWrappers.forEach(function (el, idx) {
                        var _a, _b;
                        var left = el.offsetLeft;
                        var w = (_b = (_a = items_1[idx]) === null || _a === void 0 ? void 0 : _a.w) !== null && _b !== void 0 ? _b : 0;
                        var clusterKey;
                        for (var _i = 0, _c = Array.from(colWidthMap_1.keys()); _i < _c.length; _i++) {
                            var key = _c[_i];
                            if (Math.abs(left - key) <= COL_CLUSTER_THRESHOLD_1) {
                                clusterKey = key;
                                break;
                            }
                        }
                        if (clusterKey !== undefined) {
                            colWidthMap_1.set(clusterKey, Math.max(colWidthMap_1.get(clusterKey), w));
                        }
                        else {
                            colWidthMap_1.set(left, w);
                        }
                    });
                    var numColumns = colWidthMap_1.size || 1;
                    var colWidths = Array.from(colWidthMap_1.values());
                    if (numColumns > 0 && items_1.length > 0) {
                        var totalColWidth = colWidths.reduce(function (sum, w) { return sum + w; }, 0);
                        // Dynamic buffer that scales with number of columns for Safari/Firefox
                        // Base buffer + additional per column to handle browser rendering differences
                        var buffer = 25 + numColumns * 10;
                        // Add extra width when tools have labels (e.g. Array Model has title-based labels)
                        var hasLabels = toolsBtn.some(function (t) { return t.title; });
                        var padding = hasLabels ? 30 : 20; // label panels: 10px left + 20px right; others: 10px each side
                        var labelBuffer = hasLabels ? 15 : 0;
                        var contentWidth = padding + totalColWidth + (numColumns - 1) * colGap + buffer + 10 + labelBuffer;
                        // Inline styles are already cleared above; read the CSS-natural width as a fallback check.
                        // If CSS renders wider than our formula (e.g. due to max-width rules), use CSS width.
                        var toolPanelStyle = getComputedStyle(toolPanel);
                        var cssWidth = parseFloat(toolPanelStyle.width);
                        if (!isNaN(cssWidth) &&
                            toolPanelStyle.width !== 'fit-content' &&
                            cssWidth + labelBuffer > contentWidth) {
                            contentWidth = cssWidth + labelBuffer;
                        }
                        // Set explicit width on tool panel to override max-width constraint
                        toolPanel.style.width = "".concat(contentWidth, "px");
                        toolPanel.style.maxWidth = "".concat(contentWidth, "px");
                        toolPanel.style.minWidth = "".concat(contentWidth, "px");
                        // Set explicit width on drawer-content for Safari/Firefox
                        drawerContent.style.width = "".concat(contentWidth, "px");
                        drawerContent.style.minWidth = "".concat(contentWidth, "px");
                        // Also set CSS variable for transform
                        container.style.setProperty('--drawer-width', "".concat(contentWidth, "px"));
                    }
                    else if (!toolPanel) {
                        // No BottomPanel rendered (e.g. LinkingCubesToolbar is showing).
                        // Clear the inline override so the CSS default --drawer-width: 100% applies,
                        // ensuring translateX(100%) slides the full container off-screen.
                        container.style.removeProperty('--drawer-width');
                    }
                }
                else if (container && drawerContent && toolPanel && toolsBtn.length === 0) {
                    // BottomPanel is mounted but has no tools (user just deselected the
                    // active manipulative — common in IDM sessions where the drawer
                    // stays open for the tray counter list). Clear any inline widths
                    // we set during the previous selection so drawer-content can shrink
                    // back to its natural fit-content size, which then lets the
                    // trayCounterList collapse to a single column.
                    prevBtnIdsRef.current = '';
                    toolPanel.style.width = '';
                    toolPanel.style.maxWidth = '';
                    toolPanel.style.minWidth = '';
                    drawerContent.style.width = '';
                    drawerContent.style.minWidth = '';
                    container.style.removeProperty('--drawer-width');
                }
            });
        }, 50);
        return function () { return clearTimeout(timeoutId); };
    }, [toolsBtn, isOpen, playerIconZoom, pos, language]);
    // Keep drawer open while linking cubes exist or linkingCubes tool is selected
    var hasNonLinkingCubeSelected = selectedTool.length > 0 && !selectedTool.every(function (s) { return s.startsWith('linkingCube'); });
    var isLinkingCubeToolSelected = selectedTool.length > 0 && selectedTool.every(function (s) { return s.startsWith('linkingCube'); });
    // When the user selects a non-LC item (e.g. NMP), suppress the LC toolbar until
    // they explicitly re-select a cube. Without this, clearing selection (e.g. after
    // breaking an NMP piece) would jump back to the LC toolbar because hasLinkingCubes
    // is permanently true once cubes are on canvas.
    var _v = useState(false), suppressLcToolbar = _v[0], setSuppressLcToolbar = _v[1];
    useEffect(function () {
        if (hasNonLinkingCubeSelected) {
            setSuppressLcToolbar(true);
        }
        else if (isLinkingCubeToolSelected) {
            setSuppressLcToolbar(false);
        }
    }, [hasNonLinkingCubeSelected, isLinkingCubeToolSelected]);
    // Clear stale --drawer-width before paint so the close animation uses the correct 100% default.
    // Runs synchronously between render and browser paint (before the CSS transition starts).
    useLayoutEffect(function () {
        if (isLinkingCubeToolSelected || hasLinkingCubes) {
            var container = document.querySelector('.drawer-container');
            if (container)
                container.style.removeProperty('--drawer-width');
        }
    }, [isLinkingCubeToolSelected, hasLinkingCubes, isOpen]);
    useEffect(function () {
        if ((hasLinkingCubes || isLinkingCubeToolSelected) && !hasNonLinkingCubeSelected && !suppressLcToolbar) {
            setIsOpen(true);
        }
    }, [hasLinkingCubes, hasNonLinkingCubeSelected, isLinkingCubeToolSelected, suppressLcToolbar]);
    // Focus the drawer once the slide-in transition finishes. Listening to
    // `transitionend` is more reliable than guessing a setTimeout delay, and
    // ensures focus lands after the drawer is actually on-screen.
    var prevIsOpenRef = useRef(false);
    useEffect(function () {
        var justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!justOpened)
            return;
        if (!(isLinkingCubeToolSelected || hasLinkingCubes))
            return;
        if (hasNonLinkingCubeSelected)
            return;
        var container = document.getElementById('rightSideDrawerContainer');
        if (!container)
            return;
        var done = false;
        var moveFocus = function () {
            if (done)
                return;
            done = true;
            var firstBtn = document.querySelector('.linkingCubesToolbar button:not([disabled])');
            if (firstBtn) {
                firstBtn.focus();
            }
            else {
                if (!container.hasAttribute('tabindex')) {
                    container.setAttribute('tabindex', '-1');
                }
                container.focus();
            }
        };
        var onTransitionEnd = function (e) {
            if (e.propertyName === 'transform' && e.target === container)
                moveFocus();
        };
        container.addEventListener('transitionend', onTransitionEnd);
        // Safety net: if transitionend never fires (already open, reduced motion,
        // or browser skipped the transition), move focus after 500ms.
        var fallback = setTimeout(moveFocus, 500);
        return function () {
            container.removeEventListener('transitionend', onTransitionEnd);
            clearTimeout(fallback);
        };
    }, [isOpen, isLinkingCubeToolSelected, hasLinkingCubes, hasNonLinkingCubeSelected]);
    var isFractionTileSelected = useMemo(function () {
        return selectedTool.length === 1 && selectedTool[0].startsWith('fractionTiles');
    }, [selectedTool]);
    var isNumberLineSelected = useMemo(function () {
        return selectedTool.length === 1 && selectedTool[0].startsWith('numberLine');
    }, [selectedTool]);
    var isJumpBarSelected = useMemo(function () {
        return selectedTool.length === 1 && selectedTool[0].startsWith('jumpBar_');
    }, [selectedTool]);
    useEffect(function () {
        if (isFractionTileSelected && !isOpen) {
            setIsOpen(true);
        }
    }, [isFractionTileSelected]);
    useEffect(function () {
        if (isNumberLineSelected && !isOpen) {
            setIsOpen(true);
        }
    }, [isNumberLineSelected]);
    useEffect(function () {
        if (isJumpBarSelected && !isOpen) {
            setIsOpen(true);
        }
    }, [isJumpBarSelected]);
    // NumberFrame / Counter flow uses native Tab between radiogroups (color → shape → tokens),
    // so the outer roving toolbar is disabled while this flow is active. Every other
    // manipulative keeps the existing roving/toolbar behavior untouched.
    var inNumberFrameFlow = isNumberFrameSelected || isCounterSelected;
    // Esc from the token-setting-btn / drawer-toggle-btn returns focus to the
    // currently-selected number-frame widget on the canvas. Only applies when
    // this flow is active — other manipulatives keep their default Esc behavior.
    var handleFrameControlEsc = useCallback(function (e) {
        var _a, _b;
        if (e.key !== 'Escape')
            return;
        if (!inNumberFrameFlow)
            return;
        var frameId = (_a = selectedTool.find(function (id) { return id.startsWith('numberFrame_') && !id.endsWith('_dummyForRightTool'); })) !== null && _a !== void 0 ? _a : (_b = manipulative.find(function (m) { return m.data.type === 'numberFrames' && !String(m.data.id).endsWith('_dummyForRightTool'); })) === null || _b === void 0 ? void 0 : _b.data.id;
        if (!frameId)
            return;
        var el = document.querySelector(".numberFramesWidget[data-manipulative-id=\"".concat(frameId, "\"]"));
        if (!el)
            return;
        e.preventDefault();
        e.stopPropagation();
        el.focus();
    }, [inNumberFrameFlow, selectedTool, manipulative]);
    // When entering the NumberFrame flow, the roving observer had been setting
    // tabIndex=-1 on descendant buttons. After disconnecting (via null ref below),
    // those values stay in the DOM because React only manages explicit tabIndex props.
    // Reset them so native buttons (drawer-toggle, token-setting, editNumberFrame) are
    // Tab-reachable again. Only runs for the NumberFrame/Counter flow.
    useLayoutEffect(function () {
        if (!inNumberFrameFlow)
            return;
        var container = document.getElementById('rightSideDrawerContainer');
        if (!container)
            return;
        var buttons = container.querySelectorAll('button');
        buttons.forEach(function (btn) {
            if (btn.getAttribute('role') === 'radio')
                return; // radiogroup manages its own
            if (btn.closest('[inert]'))
                return;
            if (btn.tabIndex === -1)
                btn.removeAttribute('tabindex');
        });
    }, [inNumberFrameFlow, isOpen, tokenPanelOpen.top, tokenPanelOpen.bottom, hasRealNumberFrame]);
    return (_jsxs("div", { className: "drawer-container ".concat(isOpen ? 'drawer-open' : 'drawer-closed', " ").concat(displayMode === 'smartBoard' ? 'smart-board-mode' : '', " ").concat(isLargeMode ? 'large-mode' : '', " ").concat(isCoinsDrawerActive ? 'coinsAndCountersDrawer' : '', " ").concat(hasContent || isNumberFrameSelected || isCounterSelected
            ? 'has-right-content'
            : ''), id: "rightSideDrawerContainer", role: inNumberFrameFlow ? undefined : 'toolbar', "aria-label": inNumberFrameFlow ? undefined : t('text.contextualTools'), "aria-orientation": inNumberFrameFlow ? undefined : 'vertical', ref: function (el) {
            // Pass null when in NumberFrame flow — this disconnects the roving observer
            // so the native Tab order through our radiogroups/token groups works.
            if (inNumberFrameFlow) {
                rightPanelRoving.groupProps.ref(null);
            }
            else {
                rightPanelRoving.groupProps.ref(el);
            }
            // WCAG 2.4.3 – keep the ref updated so popups can return focus here on close
            popupReturnFocusRef.current = el;
        }, tabIndex: inNumberFrameFlow ? undefined : rightPanelRoving.groupProps.tabIndex, onKeyDown: function (e) {
            var _a, _b;
            // Escape always closes the drawer, even in NumberFrame flow
            if (e.key === 'Escape' && isOpen) {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                announce(t('text.linkingCubesDrawerClosed'));
                requestAnimationFrame(function () {
                    var canvas = document.querySelector('.whitepaper');
                    canvas === null || canvas === void 0 ? void 0 : canvas.focus();
                });
                return;
            }
            // In NumberFrame flow the roving observer is disconnected, so skip
            // its onKeyDown handler.
            if (inNumberFrameFlow)
                return;
            (_b = (_a = rightPanelRoving.groupProps).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }, onFocus: inNumberFrameFlow ? undefined : rightPanelRoving.groupProps.onFocus, style: __assign(__assign({}, pos), { height: "calc(100dvh - ".concat(Number(pos.toreduce) || 0, "px)"), '--drawer-height': "calc(100dvh - ".concat(Number(pos.toreduce) || 0, "px)") }), children: [(isNumberFrameSelected || isNumberFrameCounterSelected) && (_jsx("div", { className: "tokenSettingBtnWrapper token-setting-top", inert: !isOpen, children: _jsxs("div", { className: "token-setting-slider ".concat(tokenPanelOpen.top ? 'open' : '', " ").concat(sliderTransitionEnabled ? 'transition-enabled' : ''), children: [_jsx("button", { className: "token-setting-btn ".concat(tokenPanelOpen.top ? 'active' : ''), onClick: function () { return setTokenPanelOpen(function (prev) { return (__assign(__assign({}, prev), { top: !prev.top })); }); }, onKeyDown: handleFrameControlEsc, "aria-label": "".concat(t('text.tokenSettings'), " ").concat(t('text.groupA')), "aria-expanded": tokenPanelOpen.top, children: _jsx("img", { src: getImageSrc(tokenPanelOpen.top
                                    ? './assets/images/icons/singleArrowRighPanelClose.svg'
                                    : './assets/images/icons/singleArrowRighPanelOpen.svg'), className: "toggle-icon", alt: tokenPanelOpen.top ? 'Close' : 'Open', draggable: "false" }) }), _jsx(TokenSettingPanel, { panelType: "top", tokenSelections: tokenSelections, setTokenSelections: setTokenSelections, inactive: !tokenPanelOpen.top })] }) })), _jsx("button", { className: "drawer-toggle-btn", style: isOpen && (isNumberFrameSelected || isNumberFrameCounterSelected)
                    ? { zIndex: 1 }
                    : undefined, onClick: function () {
                    var newOpen = !isOpen;
                    setIsOpen(newOpen);
                }, onKeyDown: handleFrameControlEsc, "aria-label": isOpen ? 'Close drawer' : 'Open drawer', children: isOpen ? (ClosedIconSrc ? (_jsx("img", { src: getImageSrc(ClosedIconSrc), className: "toggle-icon", alt: "Open", draggable: "false" })) : (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentCocaleor", children: [_jsx("polyline", { points: "13 17 18 12 13 7" }), _jsx("polyline", { points: "6 17 11 12 6 7" })] }))) : OpenIconSrc ? (_jsx("img", { src: getImageSrc(OpenIconSrc), className: "toggle-icon", alt: "Close", draggable: "false" })) : (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", children: [_jsx("polyline", { points: "11 17 6 12 11 7" }), _jsx("polyline", { points: "18 17 13 12 18 7" })] })) }), (isNumberFrameSelected || isNumberFrameCounterSelected) && (_jsx("div", { className: "tokenSettingBtnWrapper token-setting-bottom", inert: !isOpen, children: _jsxs("div", { className: "token-setting-slider ".concat(tokenPanelOpen.bottom ? 'open' : '', " ").concat(sliderTransitionEnabled ? 'transition-enabled' : ''), children: [_jsx("button", { className: "token-setting-btn ".concat(tokenPanelOpen.bottom ? 'active' : ''), onClick: function () { return setTokenPanelOpen(function (prev) { return (__assign(__assign({}, prev), { bottom: !prev.bottom })); }); }, onKeyDown: handleFrameControlEsc, "aria-label": "".concat(t('text.tokenSettings'), " ").concat(t('text.groupB')), "aria-expanded": tokenPanelOpen.bottom, children: _jsx("img", { src: tokenPanelOpen.bottom
                                    ? './assets/images/icons/singleArrowRighPanelClose.svg'
                                    : './assets/images/icons/singleArrowRighPanelOpen.svg', className: "toggle-icon", alt: tokenPanelOpen.bottom ? 'Close' : 'Open', draggable: "false" }) }), _jsx(TokenSettingPanel, { panelType: "bottom", tokenSelections: tokenSelections, setTokenSelections: setTokenSelections, inactive: !tokenPanelOpen.bottom })] }) })), _jsx(TooltipPortalContext.Provider, { value: true, children: _jsxs("div", { className: "drawer-content".concat(isTrayCounterCardSelected ? ' trayCounterCard-selected' : ''), 
                    // Scoped to the Number Frame / Counter flow so other manipulatives keep
                    // their existing drawer-closed behavior untouched.
                    inert: !isOpen && (isNumberFrameSelected || isCounterSelected), children: [(isIDM || isSwp) && hasTrayCounters && (_jsx("div", { ref: trayListRef, className: "trayCounterList", role: "toolbar", "aria-label": t('text.trayCounters', { defaultValue: 'Tray counters' }), style: {
                                // Driven by the ResizeObserver effect above so the list scales
                                // from 1 column on a narrow drawer up to as many columns as the
                                // resolved width allows (each cell is ≥ 48px). `1fr` per
                                // column distributes the leftover space evenly.
                                gridTemplateColumns: "repeat(".concat(trayColumns, ", 1fr)"),
                            }, children: trayCounters.map(function (tc) { return (_jsx("button", { type: "button", className: "trayCounterItem", onClick: function () { return handleTrayCounterClick(tc.counterId); }, "aria-label": tc.counterId, draggable: true, onDragStart: function (e) {
                                    // Stash the counterId on the drag payload so the WhiteBoard
                                    // drop handler can identify which tray counter to add. A
                                    // custom MIME type keeps it from colliding with other drags
                                    // (e.g. text selections).
                                    e.dataTransfer.setData('application/x-tray-counter', tc.counterId);
                                    e.dataTransfer.effectAllowed = 'copy';
                                    e.currentTarget.classList.add('is-dragging');
                                }, onDragEnd: function (e) {
                                    e.currentTarget.classList.remove('is-dragging');
                                }, children: _jsx("img", { src: getImageSrc(tc.frontImage || tc.primaryImage), alt: "", className: "trayCounterImg", draggable: false }) }, tc.counterId)); }) })), isCoinsDrawerActive ? (
                        /* Coins & Counters drawer — see isCoinsDrawerActive computation
                           above for the gating rules. */
                        _jsx(CoinsAndCountersToolbar, {})) : !suppressLcToolbar && !hasNonLinkingCubeSelected && (isLinkingCubeToolSelected || hasLinkingCubes) ? (_jsx(LinkingCubesToolbar, {})) : (_jsx(BottomPanel, { id: "rightSideToolPanel", toolsBtn: toolsBtn, className: "rightSideToolPanel ".concat(((_b = selectedTool[0]) === null || _b === void 0 ? void 0 : _b.includes('patternShapes')) ||
                                (((_c = selectedTool[0]) === null || _c === void 0 ? void 0 : _c.startsWith('manipulativeGroup_')) &&
                                    (function () {
                                        var members = manipulative.filter(function (m) { return m.data.groupId === selectedTool[0]; });
                                        return (members.length > 0 &&
                                            members.every(function (m) { return m.data.type === 'patternShapes'; }) &&
                                            !members.some(function (m) { return m.data.type === 'coloredTiles' || m.data.type === 'linearPiece'; }));
                                    })())
                                ? 'patternShapes'
                                : '', " ").concat(((_d = selectedTool[0]) === null || _d === void 0 ? void 0 : _d.includes('arrayModel')) ? 'arrayModel' : '', "\n            ").concat(((_e = selectedTool[0]) === null || _e === void 0 ? void 0 : _e.includes('fractionTiles')) ? 'fractionTiles' : '', "\n      \n             ").concat(isNumberLineSelected ? 'numberLine' : '', "\n            } ").concat(((_f = selectedTool[0]) === null || _f === void 0 ? void 0 : _f.includes('numberPiece')) ? 'numberPiece' : '', " ").concat(((_g = selectedTool[0]) === null || _g === void 0 ? void 0 : _g.includes('moneyPiece')) ? 'moneyPiece' : '', "\n             ").concat(((_h = selectedTool[0]) === null || _h === void 0 ? void 0 : _h.includes('coloredTiles')) ||
                                ((_j = selectedTool[0]) === null || _j === void 0 ? void 0 : _j.includes('linearPiece')) ||
                                (((_k = selectedTool[0]) === null || _k === void 0 ? void 0 : _k.startsWith('manipulativeGroup_')) &&
                                    (function () {
                                        var members = manipulative.filter(function (m) { return m.data.groupId === selectedTool[0]; });
                                        return (members.length > 0 &&
                                            members.every(function (m) { return m.data.type === 'coloredTiles' || m.data.type === 'linearPiece'; }));
                                    })())
                                ? ' coloredTiles'
                                : '', "\n             ").concat(isNumberFrameSelected || isCounterSelected ? 'numberFrame' : '', "\n            "), collapsibleState: collapsibleState, drawerOpen: isOpen, children: (isNumberFrameSelected || isNumberFrameCounterSelected) && (_jsx(DraggableTokenList, { tokenSelections: tokenSelections, tokenPanelOpen: tokenPanelOpen, hasRealNumberFrame: hasRealNumberFrame })) }, "rightSideToolPanel"))] }) })] }));
};
export default RightSideToolPanel;
{
    // ${
    //   isFractionTile ? 'fractionTiles' : ''
    // }
}
{
    /* <BottomPanel
  id="rightSideToolPanel"
  key="rightSideToolPanel"
  toolsBtn={toolsBtn}
  className={`rightSideToolPanel ${
    selectedTool[0]?.includes('patternShapes') ? 'patternShapes' : ''
  }`}
  collapsibleState={collapsibleState}
  drawerOpen={isOpen}
  /> */
}
